/** @jsx jsx */

import {
  LegacyButton,
  LegacyIconButton,
  Spacer,
  Text,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import ServiceIcon from 'app/developer-v3/components/ServiceIcon';
import StatusLabel from 'app/developer-v3/components/StatusLabel';
import { APP_STATUSES_NAME } from 'app/developer-v3/constants';
import { getAppUrl, getSettingsUrl } from 'app/developer-v3/utils';

import type { AppStatus } from 'app/developer-v3/types/statuses';
import type { SemVer } from 'app/developer-v3/types/version';

const Styles = {
  layout: css`
    display: flex;
    flex-flow: row wrap;
    gap: 20px;
    padding: 15px 30px;
  `,
  logoColumn: css`
    flex: 0.2;
  `,
  popoverContent: css`
    max-width: 300px;
  `,
  settingsLink: css`
    display: inline-flex;
  `,
  statusWrapper: css`
    display: flex;
    align-items: center;
    gap: 5px;
  `,
  titleColumn: css`
    flex: 0.8;
  `,
  title: css`
    align-items: center;
    display: flex;
    gap: 8px;
    padding-bottom: 0px;
    word-break: break-all;
  `,
  versionWrapper: css`
    margin-right: 10px;
    display: flex;
    align-items: center;
  `,
};

type Props = {
  appId?: number;
  canPublish: boolean;
  imageUrl?: string;
  status: AppStatus;
  title?: string;
  version: SemVer;
};

const Summary = ({
  appId,
  canPublish,
  imageUrl,
  status,
  title,
  version,
}: Props) => {
  return (
    <section css={Styles.layout}>
      <div css={Styles.logoColumn}>
        <ServiceIcon
          serviceName={title}
          size={60}
          src={imageUrl || undefined}
        />
      </div>
      <div css={Styles.titleColumn}>
        <h3 css={Styles.title}>
          <Text type="Body3">{title}</Text>
          {appId && version && (
            <LegacyIconButton
              aria-label="Integration Settings"
              color="icon-tertiary"
              href={getSettingsUrl(appId)}
              icon="navCogFill"
            />
          )}
        </h3>
        <Spacer height={10} />
        <div css={Styles.statusWrapper}>
          <StatusLabel
            label={status.display}
            variant={status.published ? 'success' : undefined}
          />
          {status.published && status.name === APP_STATUSES_NAME.beta && (
            <StatusLabel label="Beta" variant="GrayWarm3" />
          )}
        </div>
      </div>
      {canPublish && (
        <LegacyButton
          aria-label="Navigate to the Publishing page"
          href={`${getAppUrl(appId)}/publishing`}
          isFullWidth={true}
        >
          Publish
        </LegacyButton>
      )}
    </section>
  );
};

Summary.defaultProps = {
  canPublish: false,
  selectedApi: '',
  status: '',
  title: 'Integration Name',
  version: '1.0.0',
};

export default Summary;
